import { z } from 'zod';
import { uuidSchema } from './common.schema.js';

export const BaseCareUnitSchema = z.object({
	care_unit_id: uuidSchema,
	facility_id: uuidSchema,
	facility_name: z.string().optional(),
	hospital_id: uuidSchema,
	tenant_id: uuidSchema,
	unit_code: z.string().max(50),
	unit_name: z.string().max(200),
	unit_type: z.string().max(100),
	unit_level: z.number().int().nullable().optional(),
	bed_capacity: z.number().int(),
	is_active: z.boolean(),
	created_at: z.string(),
	updated_at: z.string(),
});

export type BaseCareUnit = z.infer<typeof BaseCareUnitSchema>;

/** Schema used to parse raw Prisma rows (Date -> ISO string) before returning */
export const CareUnitsResponseSchema = BaseCareUnitSchema.extend({
	created_at: z.coerce.date().transform((d) => d.toISOString()),
	updated_at: z.coerce.date().transform((d) => d.toISOString()),
});

export type CareUnitsResponse = z.infer<typeof CareUnitsResponseSchema>;

export const CareUnitTypeEnum = z.enum([
	'NICU',
	'PICU',
	'CVICU',
	'ICU',
	'SCBU',
	'Pediatric Cardiothoracic ICU',
	'Pediatric Step Down Unit',
	'Immediate Care Unit',
	'General Pediatric Unit',
	'Labor and Delivery',
	'Mother-Baby Unit',
	'Postpartum Unit',
	'Special Care Nursery',
	'Well-Baby Nursery',
]);

export type CareUnitType = z.infer<typeof CareUnitTypeEnum>;

const unitLevelSchema = z.number().int().min(1).max(3).nullable().optional();

function refineNicuLevel(
	data: { unitType?: string; unitLevel?: number | null },
	ctx: z.RefinementCtx,
) {
	if (data.unitType === 'NICU' && (data.unitLevel == null)) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			path: ['unitLevel'],
			message: 'unitLevel (1, 2, or 3) is required when unitType is NICU.',
		});
	}
	if (data.unitType && data.unitType !== 'NICU' && data.unitLevel != null) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			path: ['unitLevel'],
			message: 'unitLevel must be omitted (or null) for non-NICU unit types.',
		});
	}
}

export const CreateCareUnitRequestSchema = z
	.object({
		facilityId: uuidSchema,
		hospitalId: uuidSchema,
		tenantId: uuidSchema,
		unitCode: z.string().min(1).max(50),
		unitName: z.string().min(1).max(200),
		unitType: CareUnitTypeEnum,
		unitLevel: unitLevelSchema,
		bedCapacity: z.number().int().min(0).default(0),
	})
	.superRefine(refineNicuLevel);

export type CreateCareUnitRequest = z.infer<typeof CreateCareUnitRequestSchema>;

export const UpdateCareUnitRequestSchema = z
	.object({
		unitCode: z.string().min(1).max(50).optional(),
		unitName: z.string().min(1).max(200).optional(),
		unitType: CareUnitTypeEnum.optional(),
		unitLevel: unitLevelSchema,
		bedCapacity: z.number().int().min(0).optional(),
	})
	.superRefine((data, ctx) => {
		// unitLevel (including explicit null) requires unitType so the NICU rule
		// can be validated here rather than hitting the DB check constraint.
		if (data.unitLevel !== undefined && data.unitType === undefined) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ['unitType'],
				message: 'unitType is required when unitLevel is provided.',
			});
		}
	})
	.superRefine(refineNicuLevel);

export type UpdateCareUnitRequest = z.infer<typeof UpdateCareUnitRequestSchema>;